import GBaseModal from '@/components/g-base-modal.vue'
import { showSyncModal } from '@/components/syncmodal'
import { translateLang } from '@/data/i18n/translateLang'
import {
  IBigPlank,
  PartType,
  PointType,
  VirtualCompositePlankPos,
} from '@/partTypes'

import { toDecimal } from './commonFuncs'
import { dealPlankPoly } from './dealPaibanData'
import { computeConvexHull } from './graphCalc'
import { getPathMinMaxPoint } from './plankCommonFuncs'
import { createNewPlank } from './plankCommonFuncs'

/**
 * 判断板件是否是对纹板件
 * 检查 textureGrainInfo 是否存在且为非空对象
 */
export function isTextureAlignmentPlank(part: PartType): boolean {
  return !!(
    part?.textureGrainInfo && Object.keys(part.textureGrainInfo).length > 0
  )
}

/**
 * 清除对纹信息，接受小板或小板数组
 * 传入大板需要在调用方通过 .parts 解出后再传
 */
export function clearTextureGrainInfo(parts: PartType | PartType[]): void {
  const list = Array.isArray(parts) ? parts : [parts]
  list.forEach((p) => {
    p.textureGrainInfo = undefined
  })
}

interface Shape {
  id: string | number
  points: PointType[] // 已扩板的 rect
}

/**
 * 计算多边形顶点集合的包围盒
 */
function getBounds(points: PointType[]) {
  const [minX, minY, maxX, maxY] = getPathMinMaxPoint(points)
  return { minX, minY, maxX, maxY }
}

/**
 * 解决已膨胀图形之间的冲突，计算每个图形需要向右和向上的最小位移。
 *
 * 规则：
 * - 左下角为基准点，图形只能向右、向上移动。
 * - 保持图形间的原始设计间隙（统一值）。
 * - 只推动投影区间有重叠的图形，连锁反应传播最小位移。
 *
 * @param shapes  已外扩的图形数组
 * @param gap     期望的统一间隙（水平/垂直相同）
 * @returns       与输入顺序对应的位移数组
 */
export function resolveCollisions(shapes: Shape[], gap = 0) {
  const n = shapes.length
  const bounds = shapes.map((s) => getBounds(s.points))
  const dx = new Array(n).fill(0)
  const dy = new Array(n).fill(0)

  let hasCollision = true
  let iterations = 0
  const maxIterations = 100

  while (hasCollision && iterations < maxIterations) {
    hasCollision = false
    iterations++

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        // 使用当前位移计算实际位置
        const ix = bounds[i].minX + dx[i]
        const iy = bounds[i].minY + dy[i]
        const iw = bounds[i].maxX - bounds[i].minX
        const ih = bounds[i].maxY - bounds[i].minY

        const jx = bounds[j].minX + dx[j]
        const jy = bounds[j].minY + dy[j]
        const jw = bounds[j].maxX - bounds[j].minX
        const jh = bounds[j].maxY - bounds[j].minY

        // 检测重叠
        const overlapX = Math.max(
          0,
          Math.min(ix + iw, jx + jw) - Math.max(ix, jx)
        )
        const overlapY = Math.max(
          0,
          Math.min(iy + ih, jy + jh) - Math.max(iy, jy)
        )

        if (overlapX > 0 && overlapY > 0) {
          hasCollision = true

          // 选择推动距离最小的方向
          const needX = overlapX + gap
          const needY = overlapY + gap

          if (needX <= needY) {
            // 水平分离
            if (ix + iw / 2 < jx + jw / 2) {
              // i在左，j在右
              dx[i] -= needX / 2
              dx[j] += needX / 2
            } else {
              dx[i] += needX / 2
              dx[j] -= needX / 2
            }
          } else {
            // 垂直分离
            if (iy + ih / 2 < jy + jh / 2) {
              // i在上，j在下
              dy[i] -= needY / 2
              dy[j] += needY / 2
            } else {
              dy[i] += needY / 2
              dy[j] -= needY / 2
            }
          }
        }
      }
    }
  }

  return shapes.map((v, i) => ({ id: v.id, dx: dx[i], dy: dy[i] }))
}

/** 判断是否是对纹虚拟组合板 */
export function isVirtualCompositePlank(part: PartType): boolean {
  return !!part._isVirtualCompositePlank
}

/**
 * @description 获取大板内与指定板件同对纹组的所有小板
 * @param part      参照板件
 * @param allParts  大板内所有小板（bigPlank.parts）
 * @returns 同组的所有小板（至少包含 part 自身）
 */
export function getSameGroupParts(
  part: PartType,
  allParts: PartType[]
): PartType[] {
  const gid = part.textureGrainInfo?.groupId
  const groupLabel = part.textureGrainInfo?._groupLabel
  if (!gid) return [part]
  return allParts.filter(
    (p) =>
      p.textureGrainInfo?.groupId === gid &&
      p.textureGrainInfo._groupLabel === groupLabel
  )
}

export interface PaibanResultItem {
  children?: PaibanResultItem[]
  startX?: number
  startY?: number
  surplus?: any
  otherPlate?: any
  stockNum?: any
  stockKey?: string
  [key: string]: any
}

/**
 * 分解排版结果中的对纹虚拟组合板
 * 父板删除，子板释放为正常板件，继承父板的 surplusInfo/otherPlate/stockNum/stockKey，位置为父板 + 子板偏移
 */
export function decompositePaibanResult(
  data: PaibanResultItem[]
): PaibanResultItem[] {
  const result: PaibanResultItem[] = []
  for (const item of data) {
    if (item.children?.length) {
      for (const child of item.children) {
        result.push({
          ...child,
          startX: (item.startX ?? 0) + (child.startX ?? 0),
          startY: (item.startY ?? 0) + (child.startY ?? 0),
          surplusInfo: item.surplusInfo,
          otherPlate: item.otherPlate,
          stockNum: item.stockNum,
          stockKey: item.stockKey,
        })
      }
    } else {
      result.push(item)
    }
  }
  return result
}

/**
 * 分离对纹板件，按 groupId 分组，只保留能成套的数量
 * @param parts           小板数组
 * @param isClean         是否清除不成套板件的对纹信息
 * @param isClassifyById  为 true 时优先按 _ids 分组（复制件/扩展件自动排除）；
 *                        若无 _ids 则回退到 groupId 分组并写入 _ids。
 *                        为 false（默认）时按 groupId 分组，成套后写入 _ids。
 * @returns groups 每组恰好能成套的板件数组, normalParts, incompleteParts
 */
export function classifyTexturePlanks(
  parts: PartType[],
  isClean?: boolean,
  isClassifyById?: boolean
): {
  groups: PartType[][]
  normalParts: PartType[]
  incompleteParts: PartType[]
} {
  const textureParts: PartType[] = []
  const normalParts: PartType[] = []

  for (const part of parts) {
    if (isTextureAlignmentPlank(part)) {
      textureParts.push(part)
    } else {
      normalParts.push(part)
    }
  }

  const groups: PartType[][] = []
  const incompleteParts: PartType[] = []

  // ── 按 _ids 分组（仅当 isClassifyById=true 且存在 _ids 时生效）──
  if (
    isClassifyById &&
    textureParts.some((p) => p.textureGrainInfo?._ids?.length)
  ) {
    const idGroups = new Map<string, PartType[]>()

    for (const p of textureParts) {
      const ids = p.textureGrainInfo?._ids
      if (ids && ids.includes(p.partUniqueId)) {
        const key = ids.join('|')
        if (!idGroups.has(key)) idGroups.set(key, [])
        idGroups.get(key)!.push(p)
      } else {
        // partUniqueId 不在 _ids 中（复制件、amount 扩展件等）-> 不参与对纹分组
        incompleteParts.push(p)
      }
    }

    for (const [, group] of idGroups) {
      const allPlanks = group[0].textureGrainInfo!.allPlanks
      const setSize = allPlanks.length
      const ids = group[0].textureGrainInfo!._ids!

      // 按 allPlanks 中位置排序，确保套内板件正确配对
      group.sort(
        (a, b) =>
          allPlanks.indexOf(a.textureGrainInfo!.id) -
          allPlanks.indexOf(b.textureGrainInfo!.id)
      )

      // 检查是否成套：数量匹配且所有 _ids 成员都在
      const isComplete =
        group.length === setSize &&
        ids.every((id) => group.some((p) => p.partUniqueId === id))

      if (isComplete) {
        groups.push(group)
      } else {
        incompleteParts.push(...group)
      }
    }

    if (isClean) clearTextureGrainInfo(incompleteParts)
    return { groups, normalParts, incompleteParts }
  }

  // ── 按 groupId 分组（默认路径 / 回退路径）──
  const rawGroups = new Map<string, PartType[]>()
  for (const p of textureParts) {
    const gid = p.textureGrainInfo!.groupId
    if (!rawGroups.has(gid)) rawGroups.set(gid, [])
    rawGroups.get(gid)!.push(p)
  }

  for (const [, group] of rawGroups) {
    const allPlanks = group[0].textureGrainInfo!.allPlanks
    const setSize = allPlanks.length

    // 按套序号 + allPlanks 中位置排序，确保每套内的板件正确配对
    const occMap = new Map<string, number>()
    for (const p of group) {
      const id = p.textureGrainInfo!.id
      occMap.set(id, (occMap.get(id) ?? 0) + 1)
      ;(p as any)._setIdx = occMap.get(id)! - 1
      ;(p as any)._posIdx = allPlanks.indexOf(id)
    }
    group.sort((a, b) => {
      const d = (a as any)._setIdx - (b as any)._setIdx
      return d !== 0 ? d : (a as any)._posIdx - (b as any)._posIdx
    })

    const countMap = new Map<string, number>()
    for (const p of group) {
      const id = p.textureGrainInfo!.id
      countMap.set(id, (countMap.get(id) ?? 0) + 1)
    }
    let completeSets = Infinity
    for (const id of allPlanks) {
      completeSets = Math.min(completeSets, countMap.get(id) ?? 0)
    }

    for (let i = 0; i < completeSets; i++) {
      const set = group.slice(i * setSize, (i + 1) * setSize)
      groups.push(set)
      // 仅在 isClassifyById=true 时写入 _ids（首次进入排版页时无 _ids，走 groupId 分组并记录；
      // 后续 relayout 时有 _ids 直接按 ID 分组，不再进入此路径）
      if (isClassifyById) {
        const ids = set.map((p) => p.partUniqueId)
        for (const p of set) {
          if (p.textureGrainInfo) p.textureGrainInfo._ids = ids
        }
      }
    }

    const leftover = group.slice(completeSets * setSize)
    if (leftover.length) {
      if (isClean) clearTextureGrainInfo(leftover)
      incompleteParts.push(...leftover)
    }
  }
  return { groups, normalParts, incompleteParts }
}

/**
 * 排版流程入口预处理：将横纹对纹组的 lowPos 从「左下角原点、Y 向上」镜像翻转到「左上角原点、Y 向下」的 canvas 坐标系，写回子板。
 * 变换公式：newY = setHeight - oldY，X 保持不变，子板相对形状保持镜像一致。
 * @param parts            排版入口的全部板件
 * @param decimal          小数位，默认 2
 * @param isClassifyById   透传给 classifyTexturePlanks，控制是否按 _ids 分组
 */
export function flipTextureLowPos(
  parts: PartType[],
  decimal = 2,
  isClassifyById?: boolean
) {
  const { groups, incompleteParts } = classifyTexturePlanks(
    parts,
    undefined,
    isClassifyById
  )

  // 按 groupId + id 存储翻转后的 lowPos，供余料复用
  const flippedMap = new Map<string, Map<string, { x: number; y: number }>>()

  for (const group of groups) {
    const setSize = group[0].textureGrainInfo!.allPlanks.length
    const completeSets = Math.floor(group.length / setSize)
    for (let i = 0; i < completeSets; i++) {
      const set = group.slice(i * setSize, (i + 1) * setSize)
      if (set[0].textureGrainInfo?._lowPosFlipped) continue
      const setHeight = set.reduce((maxH, p) => {
        const y = p.textureGrainInfo?.lowPos?.y ?? 0
        const h = p.realRect?.height ?? p.rect.height
        return Math.max(maxH, y + h)
      }, 0)
      const groupId = set[0].textureGrainInfo!.groupId
      if (!flippedMap.has(groupId)) {
        flippedMap.set(groupId, new Map())
      }
      const idMap = flippedMap.get(groupId)!
      set.forEach((p) => {
        if (!p.textureGrainInfo) return
        const lp = p.textureGrainInfo.lowPos ?? { x: 0, y: 0 }
        const h = p.realRect?.height ?? p.rect.height
        const flipped = {
          x: toDecimal(lp.x, decimal),
          y: toDecimal(setHeight - lp.y - h, decimal),
        }
        p.textureGrainInfo.lowPos = flipped
        p.textureGrainInfo._lowPosFlipped = true
        idMap.set(p.textureGrainInfo.id, flipped)
      })
    }
  }

  // 余料：用相同 groupId + id 的翻转值，保证 lowPos 一致
  for (const p of incompleteParts) {
    if (!p.textureGrainInfo || p.textureGrainInfo._lowPosFlipped) continue
    const idMap = flippedMap.get(p.textureGrainInfo.groupId)
    if (idMap) {
      const flipped = idMap.get(p.textureGrainInfo.id)
      if (flipped) {
        p.textureGrainInfo.lowPos = { ...flipped }
        p.textureGrainInfo._lowPosFlipped = true
      }
    }
  }

  return parts
}

/**
 * 根据已成套的分组生成虚拟组合板
 * @param groups    每组恰好能成套的板件数组
 * @param gap       resolveCollisions 间隙，默认 0
 * @param isResolve 是否进行碰撞解析 + 位置归零，默认 true
 */
export function generateTextureCompositePlanks(
  groups: PartType[][],
  gap = 0,
  isResolve = true,
  decimal = 2
): PartType[] {
  const virtualPlanks: PartType[] = []
  const getRect = (p: PartType) => p.expandRect ?? p.rect
  const getLowPos = (p: PartType) => ({
    x: toDecimal(p.textureGrainInfo?.lowPos?.x ?? 0, decimal),
    y: toDecimal(p.textureGrainInfo?.lowPos?.y ?? 0, decimal),
  })

  for (const group of groups) {
    const setSize = group[0].textureGrainInfo!.allPlanks.length
    const completeSets = group.length / setSize

    for (let i = 0; i < completeSets; i++) {
      const set = group.slice(i * setSize, (i + 1) * setSize)

      let positions: VirtualCompositePlankPos[]
      let maxX = 0,
        maxY = 0

      if (isResolve) {
        const shapes: Shape[] = set.map((p) => {
          const rect = getRect(p)
          const lp = getLowPos(p)
          const lx = lp.x
          const ly = lp.y
          const rw = rect.width
          const rh = rect.height
          return {
            id: p.partUniqueId,
            points: [
              { x: lx, y: ly },
              { x: lx + rw, y: ly },
              { x: lx + rw, y: ly + rh },
              { x: lx, y: ly + rh },
            ],
          }
        })
        const collisions = resolveCollisions(shapes, gap)
        let minX = Infinity
        let minY = Infinity
        const rawPositions: {
          id: string
          x: number
          y: number
          w: number
          h: number
        }[] = []

        for (let j = 0; j < set.length; j++) {
          const p = set[j]
          const rect = getRect(p)
          const w = rect.width
          const h = rect.height
          const lp = getLowPos(p)
          const lx = lp.x
          const ly = lp.y
          const x = toDecimal(lx + collisions[j].dx, decimal)
          const y = toDecimal(ly + collisions[j].dy, decimal)
          rawPositions.push({ id: p.partUniqueId, x, y, w, h })
          if (x < minX) minX = x
          if (y < minY) minY = y
        }

        maxX = 0
        maxY = 0
        positions = rawPositions.map((pos) => {
          const nx = pos.x - minX
          const ny = pos.y - minY
          if (nx + pos.w > maxX) maxX = nx + pos.w
          if (ny + pos.h > maxY) maxY = ny + pos.h
          return {
            partUniqueId: pos.id,
            x: nx,
            y: ny,
            width: pos.w,
            height: pos.h,
          }
        })
      } else {
        maxX = 0
        maxY = 0
        positions = set.map((p) => {
          const lp = getLowPos(p)
          const lx = lp.x
          const ly = lp.y
          const rect = getRect(p)
          const w = rect.width
          const h = rect.height
          if (lx + w > maxX) maxX = lx + w
          if (ly + h > maxY) maxY = ly + h
          return {
            partUniqueId: p.partUniqueId,
            x: lx,
            y: ly,
            width: w,
            height: h,
          }
        })
      }

      // 计算凸包作为虚拟板的轮廓
      const allCorners: PointType[][] = positions.map((pos) => [
        { x: pos.x, y: pos.y },
        { x: pos.x + pos.width, y: pos.y },
        { x: pos.x + pos.width, y: pos.y + pos.height },
        { x: pos.x, y: pos.y + pos.height },
      ])
      const hull = computeConvexHull(allCorners)
      const path = hull.length ? [hull] : undefined
      const oPath = hull.length ? [window.$copy(hull)] : undefined

      const rep = set[0]
      // 此类信息需要拷贝一份，避免互相影响
      const slots = window.$copy(set.flatMap((p) => p.slots))
      const holes = window.$copy(set.flatMap((p) => p.holes))
      const handleSlopes = window.$copy(set.flatMap((p) => p.handleSlopes))
      const sholes = window.$copy(set.flatMap((p) => p.sholes))
      const sslots = window.$copy(set.flatMap((p) => p.sslots))
      if (hull?.length) {
        const [minx, miny, _maxx, _maxy] = getPathMinMaxPoint(hull)
        maxX = _maxx
        maxY = _maxy
      }
      const virtualPlank = createNewPlank(
        {
          matCode: rep.matCode,
          texture: rep.texture,
          thick: rep.thick,
          plankWidth: maxX,
          plankHeight: maxY,
        },
        {
          _isVirtualCompositePlank: true,
          _virtualCompositePlanks: set,
          _virtualCompositePlanksPos: positions,
          leftEdge: 0,
          rightEdge: 0,
          frontEdge: 0,
          backEdge: 0,
          plankNum: '',
          address: rep.address,
          orderNo: rep.orderNo,
          partName: rep.textureGrainInfo?.groupName ?? '未知对纹组',
          slots,
          holes,
          handleSlopes,
          sholes,
          sslots,
          process_in_engraving_to_curve: rep.process_in_engraving_to_curve,
          engraving_cut_curve: rep.engraving_cut_curve,
          engraving_cut_curve_to_turn: rep.engraving_cut_curve_to_turn,
          // path,
          // oPath,
          isUseSaw: rep.isUseSaw,
          is_high_gloss_plank: rep.is_high_gloss_plank,
          high_gloss_side: rep.high_gloss_side,
        }
      )
      virtualPlanks.push(virtualPlank)
    }
  }

  return virtualPlanks
}

/**
 * 分解虚拟组合板，还原为原始小板
 * 虚拟板自身的 _virtualCompositePlanks 会被放回数组中
 */
export function decompositeTexturePlanks(parts: PartType[]): PartType[] {
  const result: PartType[] = []
  for (const part of parts) {
    if (part._isVirtualCompositePlank && part._virtualCompositePlanks) {
      result.push(...part._virtualCompositePlanks)
    } else {
      result.push(part)
    }
  }
  return result
}

/**
 * 显示对纹信息清除确认弹窗
 * @param message   弹窗提示文案
 * @param parts     需要清除对纹信息的板件数组
 * @param onCancel  用户取消时的回调（恢复 UI 状态等）
 * @returns true=确认（已清除对纹信息）, false=取消（onCancel 已执行）
 */
export async function showTextureConfirmModal(
  message: string,
  parts: PartType[],
  onCancel?: () => void
): Promise<boolean> {
  const { status } = await showSyncModal({
    modalComponent: GBaseModal,
    title: translateLang('温馨提示'),
    contain: message,
    okText: translateLang('确认'),
    cancelText: translateLang('取消'),
  })
  if (status) {
    clearTextureGrainInfo(parts)
  } else {
    onCancel?.()
  }
  return status
}

/**
 * 如果板件是对纹虚拟组合板，则对内部所有子板执行回调
 * @param plank  待检查的板件
 * @param fn     对每个子板执行的函数
 */
export function forEachTextureChild(
  plank: PartType,
  fn: (child: PartType) => void
): void {
  if (isVirtualCompositePlank(plank)) {
    const planks = decompositeTexturePlanks([plank])
    for (const child of planks ?? []) {
      fn(child)
    }
  }
}

/** 整体变换类型：组级坐标变换 + 子板单板变换 */
export type TextureGroupTransformType = 'flipX' | 'flipY'
/**
 * 对对纹虚拟组合板做整体翻面：
 * 坐标系：组框左下角为原点，y 向上为正
 *
 * @param plank           待变换的板件，非虚拟板时退化为直接调用 childTransform
 * @param type            翻面类型：flipY=上下翻（y 关于组中线翻转），flipX=左右翻（x 关于组中线翻转）
 * @param childTransform  对每个子板执行的单板变换函数（如 rollPlank/reversalPlank）
 */
export function transformTextureGroup(
  plank: PartType,
  type: TextureGroupTransformType,
  childTransform: (child: PartType) => void
): void {
  if (!isVirtualCompositePlank(plank)) return
  const children = plank._virtualCompositePlanks ?? []
  if (!children.length) return
  const Wg = children.reduce(
    (m, c) => Math.max(m, (c.textureGrainInfo?.lowPos?.x ?? 0) + c.rect.width),
    0
  )
  const Hg = children.reduce(
    (m, c) => Math.max(m, (c.textureGrainInfo?.lowPos?.y ?? 0) + c.rect.height),
    0
  )
  // 1. 组级重算每个子板的 lowPos（持久化到子板，survives decomposite → recomposite）
  for (const child of children) {
    if (!child.textureGrainInfo) continue
    const lp = child.textureGrainInfo.lowPos
    // 为什么要用realRect，因为lowPos就是基于的成品减去封边得到的，也就是 realRect
    const rect = child.realRect
    const w = rect.width
    const h = rect.height
    if (type === 'flipY') {
      // 上下翻：y 关于组中线翻转
      child.textureGrainInfo.lowPos = { x: lp.x, y: Hg - lp.y - h }
    } else {
      // 左右翻：x 关于组中线翻转
      child.textureGrainInfo.lowPos = { x: Wg - lp.x - w, y: lp.y }
    }
  }
  // 1b. 组级重算 _virtualCompositePlanksPos（与 lowPos 同坐标系，归零，基准为 rect）
  const positions = plank._virtualCompositePlanksPos ?? []
  if (positions.length) {
    const WgPos = positions.reduce((m, p) => Math.max(m, p.x + p.width), 0)
    const HgPos = positions.reduce((m, p) => Math.max(m, p.y + p.height), 0)
    for (const pos of positions) {
      if (type === 'flipY') {
        pos.y = HgPos - pos.y - pos.height
      } else {
        pos.x = WgPos - pos.x - pos.width
      }
    }
  }
  // 2. 组级重算子板 startX/startY（排版结果返回后为大板绝对坐标，基准为 rect）
  const hasStartXY = children.some((c) => c.startX != null || c.startY != null)
  if (hasStartXY) {
    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity
    for (const c of children) {
      const sx = c.startX ?? 0
      const sy = c.startY ?? 0
      minX = Math.min(minX, sx)
      maxX = Math.max(maxX, sx + c.rect.width)
      minY = Math.min(minY, sy)
      maxY = Math.max(maxY, sy + c.rect.height)
    }
    for (const child of children) {
      const w = child.rect.width
      const h = child.rect.height
      if (type === 'flipY') {
        child.startY = minY + maxY - (child.startY ?? 0) - h
      } else {
        child.startX = minX + maxX - (child.startX ?? 0) - w
      }
    }
  }
  forEachTextureChild(plank, childTransform)
  // 3. startX/startY 变更后重算子板轮廓数据（polyArr / _plankBoundBox 等）
  if (hasStartXY) {
    for (const child of children) {
      dealPlankPoly(child)
    }
  }
}

/**
 * 组合处理对纹板件：分离 -> 分组 -> 生成虚拟板 -> 合并返回
 * @param parts            小板数组
 * @param gap              resolveCollisions 间隙，默认 0
 * @param isClean          是否清除不成套板件的对纹信息，默认 false
 * @param isResolve        是否进行碰撞解析 + 位置归零，默认 true
 * @param decimal          小数保留位数，默认 2
 * @param isClassifyById   透传给 classifyTexturePlanks / flipTextureLowPos，
 *                         true 时按 _ids 分组（复制件/扩展件自动排除），默认 false
 * @returns 合并后的数组 [...normalParts, ...incompleteParts, ...virtualPlanks]
 */
export function compositeTexturePlanks(
  parts: PartType[],
  gap = 0,
  isClean = false,
  isResolve = true,
  decimal = 2,
  isClassifyById?: boolean
): PartType[] {
  // 确保所有对纹板件（含余料）的 lowPos 已翻转，保证后续无论选谁数据都一致
  flipTextureLowPos(parts, decimal, isClassifyById)
  const { groups, normalParts, incompleteParts } = classifyTexturePlanks(
    parts,
    isClean,
    isClassifyById
  )
  const virtualPlanks = generateTextureCompositePlanks(
    groups,
    gap,
    isResolve,
    decimal
  )
  return [...normalParts, ...incompleteParts, ...virtualPlanks]
}

function getColumnLabel(n: number): string {
  let result = ''
  while (n > 0) {
    n--
    result = String.fromCharCode(65 + (n % 26)) + result
    n = Math.floor(n / 26)
  }
  return result
}

/**
 * @description 对单一大板内能成套的对纹板件生成字母标签
 * @param parts 小板数据
 */
export function genLabelTextureGroups(parts: PartType[]): void {
  // isClassifyById=true：有 _ids 时按 ID 分组（不覆盖 _ids），无 _ids 时回退到 groupId 分组
  const { groups } = classifyTexturePlanks(parts, false, true)
  let labelIdx = 0
  for (const group of groups) {
    const label = getColumnLabel(++labelIdx)
    for (const part of group) {
      if (part.textureGrainInfo) {
        part.textureGrainInfo._groupLabel = label
      }
    }
  }
}
